import { DateRenderer, DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Link from '@/components/UI/Link';
import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { calculateAge, fetchMetadataColumns } from '@/helpers';
import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import { Animal } from '@/model/Animal.model';
import { ID } from '@/model/Common.model';
import { web as webRoute } from '@/support/route';
import { isClosed } from '@/support/study';
import { generateMetadataFilterOptions } from '../Cages/Cages.utils';
import { filterOptionsFromApi } from '../Colony.utils';

export interface AnimalFiltersEntity {
  fields: Array<{
    name: string;
    value: string;
    options: Array<Record<string, number>>;
  }>;
  metadata: Array<AnimalMetadataFilter>;
}

export interface AnimalMetadataFilter {
  id: ID;
  name: string;
  slug: string;
  field_type: string;
}

export const generateColumns = ({ metadata }: { metadata: Array<any> }, columns: any) => {
  const cols = [
    {
      id: 'id',
      accessorKey: 'id',
      isVisible: false,
      lockVisibility: true,
    },
    {
      id: 'catalog',
      header: 'Animal No.',
      accessorKey: 'catalog',
      enableSorting: true,
      isVisible: columns.catalog ?? false,
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      cell: ({ row: { original } }: any) => original.catalog || '',
    },
    {
      id: 'name',
      header: 'Animal Name',
      enableSorting: true,
      accessorKey: 'name',
      isVisible: columns.name ?? false,
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      cell: ({ row: { original } }: any) => original.name || '',
    },
    {
      id: 'cage',
      header: 'Cage No.',
      enableSorting: true,
      accessorKey: 'collection.data.catalog',
      isVisible: columns.cage ?? false,
      cell: ({ row }: any) => (_isNil(row?.original?.terminated_at) ? row?.original?.cage?.catalog : 'Deceased'),
    },
    {
      id: 'collection',
      header: 'Cage Name',
      enableSorting: true,
      accessorKey: 'collection.data.name',
      isVisible: columns.collection ?? false,
      cell: ({ row }: { row: { original: Animal } }) =>
        _isNil(row?.original?.terminated_at) ? row?.original?.cage?.name : 'Deceased',
    },
    {
      id: 'deceased',
      header: 'Deceased date',
      enableSorting: true,
      isVisible: columns['deceased'] ?? false,
      accessorKey: 'terminated_at',
      cell: ({
        row: {
          original: { terminated_at },
        },
      }: {
        row: { original: Animal };
      }) => <DateRenderer value={terminated_at} defaultResponse="" />,
    },
    {
      id: 'deceased_reason',
      header: 'Deceased reason',
      enableSorting: false,
      accessorKey: 'terminated_at_data.termination_title',
      isVisible: columns['deceased_reason'] ?? false,
    },
    {
      id: 'deceased_comment',
      header: 'Deceased comment',
      enableSorting: false,
      accessorKey: 'terminated_at_data.termination_value',
      isVisible: columns['deceased_comment'] ?? false,
      cell: ({
        row: {
          original: { terminated_at_data },
        },
      }: {
        row: { original: Animal };
      }) => (
        <p className="truncate">
          {_notNil(terminated_at_data?.termination_value) ? terminated_at_data?.termination_value : ''}
        </p>
      ),
    },
    {
      id: 'tailId',
      header: 'Tail ID',
      accessorKey: 'alt_ids.tail',
      enableSorting: false,
      isVisible: columns.tailId ?? false,
    },
    {
      id: 'donorId',
      header: 'Donor ID',
      accessorKey: 'alt_ids.donor',
      enableSorting: false,
      isVisible: columns.donorId ?? false,
    },
    {
      id: 'earId',
      header: 'Ear ID',
      accessorKey: 'alt_ids.ear',
      enableSorting: false,
      isVisible: columns.earId ?? false,
    },
    {
      id: 'tagId',
      header: 'Tag ID',
      accessorKey: 'alt_ids.tag',
      enableSorting: false,
      isVisible: columns.tagId ?? false,
    },
    {
      id: 'sex',
      header: 'Sex',
      enableSorting: true,
      accessorKey: 'sex',
      isVisible: columns.sex ?? false,
      cell: ({
        row: {
          original: { sex },
        },
      }: {
        row: { original: Animal };
      }) => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (sex) {
          return sex === 'f' ? 'Female' : 'Male';
        }
        return '';
      },
    },
    {
      id: 'dob',
      header: 'D.O.B',
      enableSorting: true,
      accessorKey: 'dob',
      isVisible: columns.dob ?? false,
      cell: ({ row: { original } }: { row: { original: Animal } }) => (
        <DateRenderer value={original.dob} defaultResponse="" />
      ),
    },
    {
      id: 'time',
      header: 'Age',
      enableSorting: true,
      accessorKey: 'dob',
      isVisible: columns.time ?? false,
      cell: ({
        row: {
          original: { dob, terminated_at: terminatedAt },
        },
      }: {
        row: { original: Animal };
      }) => (_notNil(dob) ? calculateAge(dob, terminatedAt) : ''),
    },
    {
      id: 'species',
      header: 'Species',
      enableSorting: true,
      accessorKey: 'species_name',
      isVisible: columns.species ?? false,
    },
    {
      id: 'strain',
      header: 'Strain',
      enableSorting: true,
      accessorKey: 'strain_name',
      isVisible: columns.strain ?? false,
    },
    {
      id: 'date',
      header: 'Created',
      accessorKey: 'created_at',
      enableSorting: true,
      isVisible: columns.date ?? false,
      cell: ({ row: { original } }: { row: { original: Animal } }) => <DateTimeRenderer value={original.created_at} />,
    },
    {
      id: 'study',
      header: 'Study',
      accessorKey: 'study_id',
      enableSorting: true,
      isVisible: columns.study ?? false,
      cell: ({ row: { original } }: { row: { original: Animal } }) => {
        const studyData = (original as any)['study_link'];
        const studyName = studyData?.name ?? '';
        return _notNil(studyData?.access) ? (
          <Link className="link blue" to={webRoute('studies.show', { id: studyData?.id })}>
            {studyName}
          </Link>
        ) : (
          studyName
        );
      },
    },
    ...fetchMetadataColumns({ metadata, columns }),
  ];

  return cols;
};

export const validateManageAnimalsSelection = (
  validation: {
    hasWritePermission: boolean | true;
    isNotDeceased: boolean | true;
    hasActiveStudy: boolean | false;
    hasIdenticalStudy: boolean | false;
  },
  selectedAnimals: Array<Animal> | undefined = [],
  limit: number = 300,
  inReview: boolean = false
): { disabled: boolean; tooltip: string } => {
  const defaultResult = {
    disabled: false,
    tooltip: '',
  };

  if (_isEmpty(selectedAnimals)) {
    return defaultResult;
  }

  if (inReview) {
    return {
      disabled: true,
      tooltip: `You cannot manage animals while the study is in review`,
    };
  }

  if (selectedAnimals.length > limit) {
    return {
      disabled: true,
      tooltip: `You can only manage ${limit} animals at a time`,
    };
  }

  if (validation.hasWritePermission) {
    const hasPermissions = selectedAnimals.every(
      (a) => (a as any)['study_link']?.access === 'write' || _isNil((a as any)['study_id'])
    );

    if (_isNotEmpty(selectedAnimals) && !hasPermissions) {
      return {
        disabled: true,
        tooltip: 'You must have at least write access to the study to manage animals',
      };
    }
  }

  if (validation.isNotDeceased) {
    const hasDeceased = selectedAnimals.some(
      (animal) =>
        _notNil(animal.terminated_at) || _isNil(animal.cage_id) || (animal as any).collection?.data?.name === 'Deceased'
    );
    if (hasDeceased) {
      return {
        disabled: true,
        tooltip: 'This action cannot be performed on deceased animals',
      };
    }
  }

  if (validation.hasIdenticalStudy && !animalsInSameStudy(selectedAnimals)) {
    return {
      disabled: true,
      tooltip: 'All animals must be assigned to the same study',
    };
  }

  if (validation.hasActiveStudy) {
    const hasClosedStudy = selectedAnimals.some((animal) => isClosed((animal as any)['study_link']?.data));

    if (hasClosedStudy) {
      return {
        disabled: true,
        tooltip: 'Animals must not be assigned to archived or completed studies',
      };
    }
  }

  return defaultResult;
};

const animalsInSameStudy = (animals: Array<Animal>) => {
  const studyId = animals.find(Boolean)?.study_id;

  return !animals.some((animal) => animal.study_id !== studyId);
};

export const selectedAnimalsStudyIds = (selectedAnimals: any) => [
  ...new Set(
    selectedAnimals.reduce((studyIds: Array<ID>, animal: Animal) => {
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      if (animal?.study_id) {
        studyIds.push(animal.study_id);
      }
      return studyIds;
    }, [])
  ),
];

export const animalsHaveStudies = (selectedAnimals: any) =>
  selectedAnimalsStudyIds(selectedAnimals).some((studyId) => _notNil(studyId));

export const generateFilterOptions = (filters: AnimalFiltersEntity) => {
  const result = _isNotEmpty(filters.fields)
    ? filters.fields
        .filter((field: any) => field.name !== 'Cage')
        .map(({ name: animalFilterName, value, options }) => {
          const fetchFromApi = filterOptionsFromApi(animalFilterName);
          if (fetchFromApi) {
            return {
              value,
              name: animalFilterName,
              operations: [
                {
                  name: 'is equal to',
                  value: filterType.eq,
                  options: [],
                },
                {
                  name: 'is not equal to',
                  value: filterType.ne,
                  options: [],
                },
              ],
              fetchFromApi: {
                placeholder: 'Search studies',
                url: 'studies.base',
              },
            };
          } else {
            return {
              value,
              name: animalFilterName,
              operations: [
                {
                  name: 'is equal to',
                  value: filterType.eq,
                  options: options,
                },
                {
                  name: 'is not equal to',
                  value: filterType.ne,
                  options: options,
                },
              ],
            };
          }
        })
    : [];

  const metadataFilterOptions = generateMetadataFilterOptions(filters.metadata);
  if (_isNotEmpty(metadataFilterOptions)) {
    result.push(...(metadataFilterOptions as any));
  }
  return result;
};

export const reducer = (state: any, action: any) => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.data,
      };
    case 'SET_UPDATING':
      return {
        ...state,
        updating: action.data,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.data,
      };
    case 'SET_DATA':
      return {
        ...state,
        data: action.data,
      };
    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        searchQuery: action.data,
        current_page: 1,
      };
    case 'SET_SELECTED':
      return {
        ...state,
        selectedRows: action.data,
      };
    case 'SET_PAGING':
      return {
        ...state,
        current_page: state.per_page === action.data.per_page ? action.data.current_page : 1,
        per_page: action.data.per_page,
      };
    case 'SET_FILTERS':
      return {
        ...state,
        filters: action.data.filters,
        filterMatch: action.data.filterMatch,
        current_page: 1,
      };
    case 'SET_SORT':
      return {
        ...state,
        sort: action.data['sort'],
        order: action.data.order,
      };
    case 'SET_FILTER_OPTIONS':
      return {
        ...state,
        filterOptions: action.data,
      };
    case 'SET_COLUMNS':
      return {
        ...state,
        columns: action.data,
      };
    case 'SET_METADATA':
      return {
        ...state,
        metadata: action.data,
      };
    default: {
      return { ...state };
    }
  }
};

export const validAnimalNameLength = (name: string) => {
  if (_isEmpty(name)) {
    return false;
  }
  return name.length <= 40;
};
